import React from 'react';
import { createRoot } from 'react-dom/client'
import './index.scss'
import { BrowserRouter } from 'react-router-dom'
import { Provider, useDispatch } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web'
import { keycloak as authClient, initOptions } from './keycloak'
import { store, persistor } from './store'
import { authLogin } from './store/reducers/user'
import App from './App'
import { useEffect, useState } from 'react'

// 'teacher' is included because standard teachers created through Platform signup must be
// able to use the Mapper. Keep this list in step with hasMapperAccessRole in the backend.
const allowedMapperRoles = new Set(['admin', 'admin-teacher', 'teacher'])

function AuthHandler() {
    const { keycloak, initialized } = useKeycloak()
    const dispatch = useDispatch()
    const [authState, setAuthState] = useState<'loading' | 'allowed' | 'forbidden'>('loading')

    useEffect(() => {
        if (!initialized) {
            return
        }

        if (!keycloak.authenticated) {
            setAuthState('forbidden')
            return
        }

        const tokenParsed = (keycloak.tokenParsed || {}) as any
        const roles: string[] = tokenParsed?.realm_access?.roles || []
        const hasMapperAccess = roles.some((role) => allowedMapperRoles.has(role))

        if (!hasMapperAccess) {
            setAuthState('forbidden')
            return
        }

        dispatch(
            authLogin({
                uid: tokenParsed?.sub || '',
                user_token: keycloak.token || '',
            })
        )
        setAuthState('allowed')
    }, [initialized, keycloak, dispatch])

    // Ends the Keycloak SSO session, otherwise the next person on a shared device is
    // signed straight into this user's Website/Platform session.
    const handleLogout = () => {
        if (keycloak.authenticated) {
            keycloak.logout()
        } else {
            window.location.reload()
        }
    }

    if (!initialized || authState === 'loading') return <></>
    if (authState === 'forbidden') return (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', background:'#f5f5f5', fontFamily:'sans-serif' }}>
            <div style={{ background:'white', borderRadius:8, padding:40, maxWidth:520, width:'100%', boxShadow:'0 2px 12px rgba(0,0,0,0.12)' }}>
                <h2 style={{ marginTop:0 }}>You've been redirected here</h2>
                <p style={{ color:'#666', fontSize:14 }}>This is a temporary redirect page for known issues. You may have arrived here because of one of the following:</p>
                <ol style={{ fontSize:14, lineHeight:1.8 }}>
                    <li>You clicked <strong>Logout</strong> and were sent here instead of the login screen.</li>
                    <li>You tried to access the <strong>Curriculum Mapper</strong> with a role that does not have mapper access (e.g. student or parent).</li>
                    <li>Another known session or permission issue occurred.</li>
                </ol>
                <hr style={{ margin:'24px 0' }} />
                <p style={{ color:'#666', fontSize:14 }}>If you are on a shared device, log out before you leave. Otherwise, click one of the buttons below to return:</p>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                    <button onClick={handleLogout} style={{ padding:'8px 20px', background:'#1976d2', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontWeight:600 }}>Log out</button>
                    <button onClick={() => { window.location.href = 'https://platform.scienceisland.com'; }} style={{ padding:'8px 20px', background:'white', color:'#1976d2', border:'1px solid #1976d2', borderRadius:4, cursor:'pointer' }}>Platform Portal</button>
                    <button onClick={() => { window.location.href = 'https://scienceisland.com'; }} style={{ padding:'8px 20px', background:'white', color:'#1976d2', border:'1px solid #1976d2', borderRadius:4, cursor:'pointer' }}>Website</button>
                    <button onClick={() => { window.location.href = 'https://mapper.scienceisland.com'; }} style={{ padding:'8px 20px', background:'white', color:'#1976d2', border:'1px solid #1976d2', borderRadius:4, cursor:'pointer' }}>Curriculum Mapper</button>
                </div>
            </div>
        </div>
    )

    return <App />
}

const rootElement = document.getElementById('root')!
const root = createRoot(rootElement!)
root.render(
    <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
            <ReactKeycloakProvider
                authClient={authClient}
                initOptions={initOptions}
            >
                <BrowserRouter>
                    <AuthHandler />
                </BrowserRouter>
            </ReactKeycloakProvider>
        </PersistGate>
    </Provider>
)
